import React, { useState, useEffect } from "react";
import axios from "axios";
import { Bar } from "react-chartjs-2";
import { Button, Typography, Box, Grid, Card, CardContent, Skeleton } from "@mui/material";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from "chart.js";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const API_URL = process.env.REACT_APP_API_URL; // Use environment variable for the API URL

const UserAnalytics = ({ userId, onLogout }) => {

  useEffect(() => {
    window.scrollTo(0, 0);  // Scroll to top when this page is loaded
  }, []);


  
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (userId) {
      fetchAnalyticsData();
    }
  }, [userId]);

  const fetchAnalyticsData = () => {
    setLoading(true);
    setError("");
    
    axios
      .get(`${API_URL}/user/analytics/`, { params: { user_id: userId } })
      .then((response) => {
        setAnalyticsData(response.data);
      })
      .catch((error) => {
        setError("There was an error fetching the user analytics.");
        console.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const createChartData = (label, values, colors) => ({
    labels: label,
    datasets: [
      {
        label: "Analytics",
        data: values,
        backgroundColor: colors,
        borderColor: colors.map((color) => color),
        borderWidth: 1,
      },
    ],
  });

  const chartData1 = createChartData(
    ["Posts Made", "Sessions Attended (Posts)"],
    [analyticsData?.posts_count, analyticsData?.attended_sessions_post],
    ["#42A5F5", "#66BB6A"]
  );
  const chartData2 = createChartData(
    ["Posts Booked", "Sessions Attended (Bookings)"],
    [analyticsData?.total_booked_posts, analyticsData?.attended_sessions_booking],
    ["#FF7043", "#FFEB3B"]
  );
  const chartData3 = createChartData(
    ["Requests Made", "Sessions Attended (Requests)"],
    [analyticsData?.requests_made, analyticsData?.attended_sessions_request],
    ["#8E24AA", "#F44336"]
  );
  const chartData4 = createChartData(
    ["Requests Accepted", "Sessions Attended (Accepted)"],
    [analyticsData?.accepted_requests, analyticsData?.attended_sessions_accepted_requests],
    ["#D32F2F", "#1976D2"]
  );
  const chartData5 = createChartData(
    ["Requests as Student", "Sessions Attended (Student)"],
    [analyticsData?.requests_as_student, analyticsData?.attended_sessions_as_student],
    ["#388E3C", "#FFC107"]
  );
  const chartData6 = createChartData(
    ["Requests as Teacher", "Sessions Attended (Teacher)"],
    [analyticsData?.requests_as_teacher, analyticsData?.attended_sessions_as_teacher],
    ["#0288D1", "#C2185B"]
  );

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
        <Skeleton variant="rectangular" width={200} height={50} sx={{ marginBottom: 2 }} />
        <Skeleton variant="rectangular" width={345} height={200} sx={{ marginBottom: 2 }} />
        <Skeleton variant="rectangular" width={345} height={200} sx={{ marginBottom: 2 }} />
        <Skeleton variant="rectangular" width={345} height={200} sx={{ marginBottom: 2 }} />
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography
                                        variant="h3"
                                        component="h1"
                                        align="center"
                                        fontSize={{ xs: '2rem', sm: '2.5rem', md: '3rem' }}
                                        sx={{
                                          backgroundImage: 'linear-gradient(45deg, #ff00ff, #00bfff)',
                                          mt:10,
                                          WebkitBackgroundClip: 'text',  // This makes the gradient clip to the text
                                          color: 'transparent',  // Makes the text itself transparent to show the gradient
                                        }}
                                        gutterBottom
                                      >
                                        Analytics
                                      </Typography>
   

      {error && <Typography color="error">{error}</Typography>}

      <Grid container spacing={3} justifyContent="center">
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ boxShadow: 5, borderRadius: 3, backgroundColor: "#000000", color: "white" }}>
            <CardContent>
              <Typography variant="h5" sx={{ fontWeight: "bold", mb: 2 }}>
                User Analytics
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                {analyticsData?.user_data?.name} {analyticsData?.user_data?.surname}
              </Typography>
              <Typography variant="body1" sx={{ color: "gray", mb: 2 }}>
                Rating: {analyticsData?.user_data?.rating || "N/A"}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Bar Chart 1 - Posts Made vs Sessions Attended */}
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ boxShadow: 5, borderRadius: 3, backgroundColor: "#000000", color: "white" }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                Host Sessions Created Vs Attended
              </Typography>
              <Bar data={chartData1} options={{ responsive: true }} />
            </CardContent>
          </Card>
        </Grid>

        {/* Bar Chart 2 - Posts Booked vs Sessions Attended */}
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ boxShadow: 5, borderRadius: 3, backgroundColor: "#000000", color: "white" }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                Host Sessions Booked Vs Attended
              </Typography>
              <Bar data={chartData2} options={{ responsive: true }} />
            </CardContent>
          </Card>
        </Grid>

        {/* Bar Chart 3 - Requests Made vs Sessions Attended */}
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ boxShadow: 5, borderRadius: 3, backgroundColor: "#000000", color: "white" }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                Request Sessions Created Vs Attended
              </Typography>
              <Bar data={chartData3} options={{ responsive: true }} />
            </CardContent>
          </Card>
        </Grid>

        {/* Bar Chart 4 - Requests Accepted vs Sessions Attended */}
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ boxShadow: 5, borderRadius: 3, backgroundColor: "#000000", color: "white" }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                Request Sessions Accepted Vs Attended
              </Typography>
              <Bar data={chartData4} options={{ responsive: true }} />
            </CardContent>
          </Card>
        </Grid>

        {/* Bar Chart 5 - Requests as Student vs Sessions Attended */}
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ boxShadow: 5, borderRadius: 3, backgroundColor: "#000000", color: "white" }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                Session Requests Sent to Users Vs Attended
              </Typography>
              <Bar data={chartData5} options={{ responsive: true }} />
            </CardContent>
          </Card>
        </Grid>

        {/* Bar Chart 6 - Requests as Teacher vs Sessions Attended */}
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ boxShadow: 5, borderRadius: 3, backgroundColor: "#000000", color: "white" }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
              Session Requests Recieved from Users Vs Attended
              </Typography>
              <Bar data={chartData6} options={{ responsive: true }} />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default UserAnalytics;
